import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type PropsWithChildren,
} from "react";
import { ChevronLeft, ChevronRight, HelpCircle, X } from "@untitledui/icons";
import { useTranslation } from "react-i18next";
import { ShipperOnboardingSlideMock } from "@/components/application/onboarding/shipper-onboarding-slide-mock";
import type { AskEveMockLabels } from "@/components/application/onboarding/shipper-onboarding-slide-mock";
import { Modal, ModalOverlay, Dialog } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import {
    SHIPPER_ONBOARDING_SECTION_I18N_KEYS,
    shipperOnboardingSeenStorageKey,
    type ShipperOnboardingSectionId,
} from "@/config/shipper-onboarding-sections";
import { useShipperOnboardingSection } from "@/hooks/use-shipper-onboarding-section";
import { storage } from "@/lib/local-storage";

type Slide = { title: string; body: string; mock?: string };

function readSlides(
    translate: (key: string, opts?: { returnObjects?: boolean }) => unknown,
    i18nKey: string
): Slide[] {
    const raw = translate(`shipper.${i18nKey}.slides`, { returnObjects: true });
    if (!Array.isArray(raw)) return [];
    return raw as Slide[];
}

export type ShipperOnboardingContextValue = {
    openHelp: () => void;
    /** True quando la route corrente ha slide onboarding per la macro-area */
    isOnboardingAvailable: boolean;
};

const ShipperOnboardingContext = createContext<ShipperOnboardingContextValue | null>(null);

export function useShipperOnboardingOptional(): ShipperOnboardingContextValue | null {
    return useContext(ShipperOnboardingContext);
}

/**
 * Pulsante tutorial da collocare nell’header di pagina (azioni a destra del titolo).
 * Non renderizza nulla se non c’è onboarding per la route corrente.
 */
export function ShipperOnboardingHeaderAction({ className }: { className?: string }) {
    const ctx = useContext(ShipperOnboardingContext);
    const { t } = useTranslation(["pages:onboarding", "common"]);

    if (!ctx?.isOnboardingAvailable) {
        return null;
    }

    return (
        <Button
            color="secondary"
            size="md"
            className={className ? `shrink-0 shadow-sm ${className}` : "shrink-0 shadow-sm"}
            iconLeading={HelpCircle}
            aria-label={t("ui.helpAria")}
            onPress={ctx.openHelp}
        />
    );
}

/**
 * Fornisce il modale onboarding e il contesto per aprirlo dagli header di pagina.
 * Montare una sola volta nel layout Shipper, avvolgendo l’area che contiene `<Outlet />`.
 */
export function ShipperOnboardingProvider({ children }: PropsWithChildren) {
    const sectionId = useShipperOnboardingSection();
    const { t } = useTranslation(["pages:onboarding", "common"]);
    const [modalOpen, setModalOpen] = useState(false);
    const [slideIndex, setSlideIndex] = useState(0);

    const i18nKey = sectionId ? SHIPPER_ONBOARDING_SECTION_I18N_KEYS[sectionId] : "";
    const slides = useMemo(() => (i18nKey ? readSlides(t, i18nKey) : []), [t, i18nKey]);
    const askEveLabels = useMemo((): AskEveMockLabels | null => {
        if (i18nKey !== "askEve") return null;
        const raw = t("shipper.askEve.mockUi", { returnObjects: true });
        if (!raw || typeof raw !== "object") return null;
        return raw as AskEveMockLabels;
    }, [t, i18nKey]);
    const total = slides.length;
    const safeIndex = total > 0 ? Math.min(slideIndex, total - 1) : 0;
    const current = slides[safeIndex];

    const isOnboardingAvailable = Boolean(sectionId && total > 0);

    const markSeen = useCallback((id: ShipperOnboardingSectionId) => {
        storage.setItem(shipperOnboardingSeenStorageKey(id), "1");
    }, []);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open && sectionId) {
                markSeen(sectionId);
            }
            setModalOpen(open);
        },
        [sectionId, markSeen]
    );

    useEffect(() => {
        setSlideIndex(0);
    }, [sectionId]);

    useEffect(() => {
        if (!sectionId) {
            setModalOpen(false);
            return;
        }
        const seen = storage.getItem(shipperOnboardingSeenStorageKey(sectionId)) === "1";
        setModalOpen(!seen);
    }, [sectionId]);

    const openHelp = useCallback(() => {
        if (!sectionId || total === 0) return;
        setSlideIndex(0);
        setModalOpen(true);
    }, [sectionId, total]);

    const goPrev = () => setSlideIndex((i) => Math.max(0, i - 1));
    const goNext = () => setSlideIndex((i) => Math.min(total - 1, i + 1));

    const finish = () => {
        if (sectionId) {
            markSeen(sectionId);
        }
        setModalOpen(false);
    };

    const dialogAriaLabel =
        sectionId && i18nKey
            ? `${t("ui.dialogIntro")}: ${t(`shipper.${i18nKey}.label`)}`
            : t("ui.dialogIntro");

    const contextValue = useMemo<ShipperOnboardingContextValue>(
        () => ({
            openHelp,
            isOnboardingAvailable,
        }),
        [openHelp, isOnboardingAvailable]
    );

    const isLast = safeIndex >= total - 1;
    const hasMock = Boolean(current?.mock);

    return (
        <ShipperOnboardingContext.Provider value={contextValue}>
            {children}
            {isOnboardingAvailable ? (
                <ModalOverlay isOpen={modalOpen} isDismissable onOpenChange={handleOpenChange}>
                    <Modal>
                        <Dialog aria-label={dialogAriaLabel}>
                            <div
                                className={`relative w-full rounded-xl bg-primary p-6 shadow-xl ring-1 ring-secondary ${
                                    hasMock ? "max-w-lg sm:max-w-2xl" : "max-w-lg sm:max-w-xl"
                                }`}
                            >
                                <div className="absolute right-3 top-3 flex gap-1">
                                    <Button
                                        color="tertiary"
                                        size="sm"
                                        aria-label={t("modal.close", { ns: "common" })}
                                        className="!p-2"
                                        onPress={() => handleOpenChange(false)}
                                    >
                                        <X data-icon className="size-5 text-fg-quaternary" />
                                    </Button>
                                </div>

                                <div className="pr-10">
                                    <p className="text-xs font-medium uppercase tracking-wide text-tertiary">
                                        {t(`shipper.${i18nKey}.label`)}
                                    </p>
                                    <h2 className="mt-1 text-lg font-semibold text-primary">{current?.title}</h2>
                                    {hasMock ? (
                                        <div className="mt-4">
                                            <ShipperOnboardingSlideMock variant={current?.mock} askEveLabels={askEveLabels} />
                                        </div>
                                    ) : null}
                                    <p className={`text-sm leading-relaxed text-secondary ${hasMock ? "mt-4" : "mt-3"}`}>
                                        {current?.body}
                                    </p>
                                </div>

                                <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                    <p className="text-xs text-quaternary tabular-nums">
                                        {t("ui.slideOf", { current: safeIndex + 1, total })}
                                    </p>
                                    <div className="flex items-center justify-center gap-1.5">
                                        {slides.map((_, i) => (
                                            <button
                                                key={i}
                                                type="button"
                                                aria-current={i === safeIndex ? "step" : undefined}
                                                aria-label={t("ui.goToSlide", { n: i + 1, total })}
                                                className={`h-2 w-2 rounded-full transition ${
                                                    i === safeIndex ? "bg-brand-solid" : "bg-quaternary/40 hover:bg-quaternary/70"
                                                }`}
                                                onClick={() => setSlideIndex(i)}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-secondary pt-4">
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        isDisabled={safeIndex === 0}
                                        iconLeading={ChevronLeft}
                                        onPress={goPrev}
                                    >
                                        {t("buttons.previous", { ns: "common" })}
                                    </Button>
                                    {isLast ? (
                                        <Button color="primary" size="sm" onPress={finish}>
                                            {t("ui.getStarted")}
                                        </Button>
                                    ) : (
                                        <Button color="primary" size="sm" iconTrailing={ChevronRight} onPress={goNext}>
                                            {t("buttons.next", { ns: "common" })}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            ) : null}
        </ShipperOnboardingContext.Provider>
    );
}
